import React from 'react';
import _ from 'lodash';

import Table from 'components/Table';
import Paginator from 'components/Paginator';
import TimeBelowDate from 'components/TimeBelowDate';
import {BookingTenantsInfo} from 'components/TenantInfo';
import ClickIconWithNoPropagation from 'components/ClickIconWithNoPropagation';

import {BOOKING_TYPE} from 'ducks/notes';

import './styles.css';

const STATUS_TO_COLOR_MAPPER = {
  Resolved: {
    backgroundColor: '#D4F1CA',
    textColor: '#228100'
  },
  Unresolved: {
    backgroundColor: '#FFC9C9',
    textColor: '#C14747'
  },
  'N/A': {
    backgroundColor: '#E1E8F0',
    textColor: '#6E7B8C'
  }
};

const NoteStatus = ({status}) => {
  const {backgroundColor, textColor} = STATUS_TO_COLOR_MAPPER[status];

  return (
    <div
      className="NoteStatus  colab-centered"
      style={{
        backgroundColor,
        border: `0.1rem solid ${textColor}`
      }}>
      <p className="colab-bold" style={{color: textColor}}>
        {status}
      </p>
    </div>
  );
};

interface NoteProps {
  primaryTenant?: {
    email: string;
    fullName: string;
    initials: string;
    avatarCropped: string | null;
    internalAvatarCropped: string | null;
  };
  secondaryTenant?: {
    email: string;
    fullName: string;
    initials: string;
    avatarCropped: string | null;
    internalAvatarCropped: string | null;
  };
  roomNumber: string;
  text: string;
  status: string;
  type: string;
  createdAt: string;
  resolvedAt: string;
  createdBy: {
    fullName: string;
  };
  resolvedBy: {
    fullName: string;
  };
  relatedObjectId: number;
}

interface NoteRowProps {
  note: NoteProps;
  openNotesModal: (noteParams) => void;
  redirectToBookingDetail: ({bookingId}: {bookingId: number}) => void;
}

const NoteRow = ({
  note: {
    primaryTenant,
    secondaryTenant,
    roomNumber,
    text,
    status,
    type,
    createdAt,
    resolvedAt,
    createdBy,
    resolvedBy,
    relatedObjectId
  },
  redirectToBookingDetail,
  openNotesModal
}: NoteRowProps) => (
  <Table.Row
    onClick={() => {
      type === BOOKING_TYPE &&
        redirectToBookingDetail({bookingId: relatedObjectId});
    }}>
    <Table.Cell className="tenant">
      {primaryTenant && (
        <BookingTenantsInfo
          tenants={[primaryTenant, secondaryTenant].filter(x => !!x)}
        />
      )}
    </Table.Cell>
    <Table.Cell className="room">{roomNumber}</Table.Cell>
    <Table.Cell className="text">
      <ClickIconWithNoPropagation
        onClick={() => openNotesModal({type, relatedObjectId})}>
        <div className="textContainer">{text}</div>
      </ClickIconWithNoPropagation>
    </Table.Cell>
    <Table.Cell className="status">
      <NoteStatus status={status} />
    </Table.Cell>
    <Table.Cell className="type">{type}</Table.Cell>
    <Table.Cell className="createdAt">
      <TimeBelowDate date={createdAt} />
    </Table.Cell>
    <Table.Cell className="resolvedAt">
      {resolvedAt && <TimeBelowDate date={resolvedAt} />}
      {!resolvedAt && `N/A`}
    </Table.Cell>
    <Table.Cell className="createdBy colab-bold">
      {_.get(createdBy, 'fullName')}
    </Table.Cell>
    <Table.Cell className="resolvedBy colab-bold">
      {_.get(resolvedBy, 'fullName', 'N/A')}
    </Table.Cell>
  </Table.Row>
);

interface PaginationProps {
  elementsCount: number;
  offset: number;
  pageSize: number;
  pageSizes: number[];
}

interface NotesTableProps {
  notes: Array<NoteProps>;
  redirectToBookingDetail: ({bookingId}: {bookingId: number}) => void;
  pagination: PaginationProps;
  onPageChange: (
    {
      offset,
      pageSize
    }: {
      offset: number;
      pageSize: number;
    }
  ) => void;
  openNotesModal: (note) => void;
}

const NotesTable = ({
  notes,
  redirectToBookingDetail,
  pagination,
  onPageChange,
  openNotesModal
}: NotesTableProps) => (
  <div className="NotesTable">
    <div className="NotesTable__header">
      <Table>
        <Table.Header>
          <Table.HeaderItem className="tenant" heading="Booking" />
          <Table.HeaderItem className="room" heading="Room No" />
          <Table.HeaderItem className="text" heading="Note" />
          <Table.HeaderItem className="status" heading="Note Status" />
          <Table.HeaderItem className="type" heading="Note Type" />
          <Table.HeaderItem className="createdAt" heading="Date created" />
          <Table.HeaderItem className="resolvedAt" heading="Date resolved" />
          <Table.HeaderItem className="createdBy" heading="Created by" />
          <Table.HeaderItem className="resolvedBy" heading="Resolved by" />
        </Table.Header>
      </Table>
    </div>
    <div className="NotesTable__body">
      <Table>
        <Table.Body>
          {notes.map((note, index) => (
            <NoteRow
              key={index}
              note={note}
              redirectToBookingDetail={redirectToBookingDetail}
              openNotesModal={openNotesModal}
            />
          ))}
        </Table.Body>
      </Table>
    </div>
    <Paginator pagination={pagination} onPageChange={onPageChange} />
  </div>
);

export default NotesTable;
